import React, { useState } from 'react';
import { Award, Target, Users, ArrowRight, X, CheckCircle, Lightbulb, BarChart3, Globe, Handshake, TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const whyChooseData = [
  {
    icon: Target,
    title: 'Strategic Focus',
    short: 'We design solutions aligned with your commercial objectives, combining creativity with precise tactical execution.',
    color: 'from-[#F4B400] to-amber-600',
    details: {
      headline: 'Design That Drives Revenue',
      description: 'Every pixel, every layout, every campaign we create is mapped to your business KPIs. We don\'t just make things look good — we make them perform.',
      points: [
        { icon: BarChart3, text: 'Data-driven design decisions backed by market research and analytics' },
        { icon: Target, text: 'Brand strategy aligned with your sales funnel and conversion goals' },
        { icon: Lightbulb, text: 'Creative campaigns engineered for measurable ROI' },
        { icon: TrendingUp, text: 'Continuous optimization based on performance metrics' }
      ],
      cta: 'Our clients see an average 40% increase in brand engagement within the first 6 months.'
    }
  },
  {
    icon: Users,
    title: 'Multidisciplinary Team',
    short: 'Designers, developers, and strategists with global experience and local focus, selected based on your needs.',
    color: 'from-purple-500 to-indigo-600',
    details: {
      headline: 'Your Dream Team, Assembled',
      description: 'We bring together specialists from diverse creative fields to deliver comprehensive solutions. No outsourcing, no middlemen — just pure talent working directly for you.',
      points: [
        { icon: Globe, text: 'Global creative talent with deep understanding of local markets' },
        { icon: Users, text: 'Graphic designers, web developers, video producers & marketing strategists' },
        { icon: Lightbulb, text: 'Each project gets a custom team assembled for your specific needs' },
        { icon: Shield, text: '10+ years of combined experience across 50+ industries' }
      ],
      cta: 'From print to digital, from concept to execution — one team handles it all.'
    }
  },
  {
    icon: Award,
    title: 'Long-Term Partnership',
    short: 'We work as part of your team, adapting and evolving to ensure sustainable growth and lasting success.',
    color: 'from-emerald-500 to-teal-600',
    details: {
      headline: 'Growing Together, Succeeding Together',
      description: 'We don\'t disappear after delivery. We become an extension of your team, evolving your brand as your business grows. Our success is measured by yours.',
      points: [
        { icon: Handshake, text: 'Dedicated account management and ongoing creative support' },
        { icon: TrendingUp, text: 'Scalable solutions that grow with your business' },
        { icon: Shield, text: 'Proactive brand evolution and market adaptation strategies' },
        { icon: CheckCircle, text: 'Priority support and exclusive client resources' }
      ],
      cta: '85% of our clients have been with us for 3+ years — that speaks volumes.'
    }
  }
];

const About = () => {
  const [selectedCard, setSelectedCard] = useState(null);

  return (
    <div className="bg-black text-white pt-24">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Who We <span className="text-[#F4B400]">Are</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A strategic partner dedicated to transforming your digital presence and driving measurable growth
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">Our Story</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                <strong className="text-black">Enrigo</strong> specializes in graphic design, web development, digital marketing, 
                and creative print and digital solutions. With over 10 years of experience, we've evolved from junior designers 
                to leading creative campaigns for both local and international corporate clients.
              </p>
              <p>
                We understand that each client is unique. That's why we offer integral solutions that combine design, 
                technology, and personalized strategy to enhance your brand and achieve measurable results.
              </p>
              <p>
                Our approach goes beyond aesthetics – we design solutions aligned with your commercial objectives, 
                combining creativity with precise tactical execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - with Popups */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Our commitment to excellence sets us apart
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseData.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <Card 
                  key={idx}
                  className="bg-white border-2 hover:border-[#F4B400] transition-all duration-300 cursor-pointer group relative overflow-hidden"
                  onClick={() => setSelectedCard(idx)}
                  data-testid={`why-choose-card-${idx}`}
                >
                  <CardContent className="p-8 text-center relative z-10">
                    <div className="bg-[#F4B400] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComp className="text-black" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-black">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {item.short}
                    </p>
                    <span className="inline-flex items-center text-[#F4B400] font-semibold text-sm group-hover:underline">
                      Learn More <ArrowRight size={14} className="ml-1" />
                    </span>
                  </CardContent>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                    style={{ backgroundImage: `linear-gradient(to right, var(--tw-gradient-from), var(--tw-gradient-to))` }}
                  />
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detail Popup Dialog */}
      <Dialog open={selectedCard !== null} onOpenChange={() => setSelectedCard(null)}>
        <DialogContent className="bg-black text-white border border-gray-800 max-w-xl sm:max-w-2xl p-0 overflow-hidden" data-testid="why-choose-popup">
          {selectedCard !== null && (() => {
            const item = whyChooseData[selectedCard];
            const IconComp = item.icon;
            return (
              <div>
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${item.color} p-8 relative`}>
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm w-14 h-14 rounded-xl flex items-center justify-center">
                      <IconComp className="text-white" size={28} />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Why Choose Us</p>
                      <h3 className="text-2xl font-bold text-white">{item.title}</h3>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-8">
                  <h4 className="text-xl font-bold text-[#F4B400] mb-3">{item.details.headline}</h4>
                  <p className="text-gray-300 leading-relaxed mb-8">{item.details.description}</p>
                  
                  <div className="space-y-4 mb-8">
                    {item.details.points.map((point, i) => {
                      const PointIcon = point.icon;
                      return (
                        <div key={i} className="flex items-start gap-4 group/point">
                          <div className="w-10 h-10 rounded-lg bg-[#F4B400]/10 flex items-center justify-center flex-shrink-0 group-hover/point:bg-[#F4B400]/20 transition-colors">
                            <PointIcon className="text-[#F4B400]" size={18} />
                          </div>
                          <p className="text-gray-300 text-sm leading-relaxed pt-2">{point.text}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA highlight */}
                  <div className="bg-[#F4B400]/10 border border-[#F4B400]/20 rounded-xl p-5">
                    <p className="text-[#F4B400] font-medium text-sm leading-relaxed">
                      {item.details.cta}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Timeline */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Our Journey</h2>
            <div className="space-y-12">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="bg-[#F4B400] text-black font-bold px-4 py-2 rounded-lg">
                    2014
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">The Beginning</h3>
                  <p className="text-gray-600">Started as a junior designer, learning the craft and building foundational skills.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="bg-[#F4B400] text-black font-bold px-4 py-2 rounded-lg">
                    2018
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Expansion</h3>
                  <p className="text-gray-600">Expanded services to include web development and digital marketing for local businesses.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="bg-[#F4B400] text-black font-bold px-4 py-2 rounded-lg">
                    2021
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">International Reach</h3>
                  <p className="text-gray-600">Began working with international corporate clients on large-scale creative campaigns.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="bg-[#F4B400] text-black font-bold px-4 py-2 rounded-lg">
                    2024
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Full-Service Agency</h3>
                  <p className="text-gray-600">Established as a comprehensive creative agency offering end-to-end solutions and corporate consulting.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
